import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from "react-router-dom";
import { API } from "aws-amplify";

import Dropdown from '../../Dropdown';
import Button from '../../Button';

import { sortArrayOfObjects, capitalizeFirstLetters, useOutsideClick } from '../../../js/Helpers';
import { getLabelColorClass } from '../utils/labelColorUtils';
import { NO_DEPARTMENT_FILTER_DISPLAY, NO_DEPARTMENT_FILTER_VALUE, isNoDepartmentFilterValue } from '../utils/departmentFilterUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTimes, faSearch, faSlidersH, faCheckSquare, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';

const normalizeFilterValue = (value) => {
	if(value === null || value === undefined || value === '' || value === 'null') return null;
	return `${value}`;
};

export default function SectionLabels({ isLoading, setIsLoading, subscreenRestricted, subscreenLocked, schoolInfo, schoolType, scheduleInfo, departments, userInfo, coursesLoading, courses, teachersLoading, teachers, sectionsLoading, sections, setSections, subsections, labels, handleMoveToNextScreen, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo }) {
	const filter_ref = useRef();
	const label_options_ref = useRef();
	const search_timeout_ref = useRef(null);

	const [selectedDepartmentID, setSelectedDepartmentID] = useState(null);
	const [searchValue, setSearchValue] = useState('');
	const [filterDataVisible, setFilterDataVisible] = useState(false);
	const [openFilterOptions, setOpenFilterOptions] = useState([]);
	const [filterOptions, setFilterOptions] = useState({
		teachers: [],
		courses: [],
		teams_labels: [{value:'none', display:'No team/label', selected:false}],
	});
	const [openLabelOptionsSectionID, setOpenLabelOptionsSectionID] = useState(null);
	const [pendingSectionLabelAdds, setPendingSectionLabelAdds] = useState({});
	const [pendingSectionLabelRemoves, setPendingSectionLabelRemoves] = useState({});

	const addSectionLabel = async (section_id, label_id) => {
		const action_key = `${section_id}-${label_id}`;
		if(pendingSectionLabelAdds[action_key] || pendingSectionLabelRemoves[action_key]) return;
		setPendingSectionLabelAdds((previous_actions) => ({...previous_actions, [action_key]: true}));

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'add', section_id:section_id, label_id:label_id};

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-section-label';
	    const myInit = {
	        response: true,
	        body: JSON.stringify(data),
	    };

		try {
			await API.post(apiName, url, myInit);

			const section_index = sections.findIndex(section => `${section.section_id}` === `${section_id}`);
			const section_info = sections[section_index];
			const section_span_id = section_info.span_id;
			const section_labels = section_info.labels || [];

			const label_index = section_labels.findIndex(label => `${label}` === `${label_id}`);
			if(label_index === -1) section_labels.push(label_id);

			if(section_span_id)
			{
				const lab_sections = sections.filter(section => `${section.span_id}` === `${section_span_id}` && `${section.section_id}` !== `${section_id}`);

				for(const lab_section of lab_sections)
				{
					const lab_section_id = lab_section.section_id;
					const lab_section_index = sections.findIndex(section => `${section.section_id}` === `${lab_section_id}`);
					const lab_section_info = (lab_section_index !== -1)  ? sections[lab_section_index] : null;
					const lab_section_labels = (lab_section_info && Array.isArray(lab_section_info.labels)) ? lab_section_info.labels : null;

					const lab_label_index = (lab_section_labels) ? lab_section_labels.findIndex(label => `${label}` === `${label_id}`) : null;
					if(lab_section_index !== -1 && lab_label_index === -1) sections[lab_section_index].labels.push(label_id);
				}
			}

			setOpenLabelOptionsSectionID(null);
			setSections([...sections]);
		} catch(e)
		{
			console.log(e.response);
		} finally
		{
			setPendingSectionLabelAdds((previous_actions) => {
				const next_actions = {...previous_actions};
				delete next_actions[action_key];
				return next_actions;
			});
		}
	};

	const removeSectionLabel = async (section_id, label_id) => {
		const action_key = `${section_id}-${label_id}`;
		if(pendingSectionLabelRemoves[action_key] || pendingSectionLabelAdds[action_key]) return;
		setPendingSectionLabelRemoves((previous_actions) => ({...previous_actions, [action_key]: true}));

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'remove', section_id:section_id, label_id:label_id};

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-section-label';
	    const myInit = {
	        response: true,
	        body: JSON.stringify(data),
	    };

		try {
			await API.post(apiName, url, myInit);

			const section_index = sections.findIndex(section => `${section.section_id}` === `${section_id}`);
			const section_info = sections[section_index];
			const section_span_id = section_info.span_id;
			const section_labels = section_info.labels || [];

			const label_index = section_labels.findIndex(label => `${label}` === `${label_id}`);
			if(label_index !== -1) section_labels.splice(label_index, 1);

			if(section_span_id)
			{
				const lab_sections = sections.filter(section => `${section.span_id}` === `${section_span_id}` && `${section.section_id}` !== `${section_id}`);

				for(const lab_section of lab_sections)
				{
					const lab_section_id = lab_section.section_id;
					const lab_section_index = sections.findIndex(section => `${section.section_id}` === `${lab_section_id}`);
					const lab_section_info = (lab_section_index !== -1)  ? sections[lab_section_index] : null;
					const lab_section_labels = (lab_section_info && Array.isArray(lab_section_info.labels)) ? lab_section_info.labels : null;

					const lab_label_index = (lab_section_labels) ? lab_section_labels.findIndex(label => `${label}` === `${label_id}`) : null;
					if(lab_section_index !== -1 && lab_label_index !== -1) sections[lab_section_index].labels.splice(lab_label_index, 1);
				}
			}

			setOpenLabelOptionsSectionID(null);
			setSections([...sections]);
		} catch(e)
		{
			console.log(e.response);
		} finally
		{
			setPendingSectionLabelRemoves((previous_actions) => {
				const next_actions = {...previous_actions};
				delete next_actions[action_key];
				return next_actions;
			});
		}
	};

	useOutsideClick(filter_ref, () => {
		setFilterDataVisible(false);
	});

	useEffect(() => {
		if(filterDataVisible) return;
		setOpenFilterOptions([]);
	}, [filterDataVisible]);

	useOutsideClick(label_options_ref, () => {
		setOpenLabelOptionsSectionID(null);
	}, ['add-label-to-section']);

	const handleSearchChange = (e) => {
		const next_value = e.target.value;

		if(search_timeout_ref.current) clearTimeout(search_timeout_ref.current);
		search_timeout_ref.current = setTimeout(() => {
			setSearchValue(next_value.trim().toLowerCase());
		}, 140);
	};

	useEffect(() => {
		return () => {
			if(search_timeout_ref.current) clearTimeout(search_timeout_ref.current);
		};
	}, []);

	const updateFilterOptions = (filter_title, option_index, selected) => {
		setFilterOptions((previous_options) => {
			const next_options = {...previous_options};
			const option_list = [...next_options[filter_title]];
			option_list[option_index] = {...option_list[option_index], selected};
			next_options[filter_title] = option_list;
			return next_options;
		});
	};

	const updateOpenFilters = (filter_title, filter_options_open) => {
		setOpenFilterOptions((previous_open_filters) => {
			if(filter_options_open) return previous_open_filters.filter(filter => filter !== filter_title);
			return [...previous_open_filters, filter_title];
		});
	};

	useEffect(() => {
		if(!teachersLoading && !coursesLoading && !sectionsLoading) setIsLoading(false);
	}, [teachersLoading, coursesLoading, sectionsLoading, setIsLoading]);

	useEffect(() => {
		if(!userInfo || subscreenRestricted || subscreenLocked) return;

		setScreenAllowsUniversalSidebar(true);
		setUniversalSidebarLoadingInfo({sidebar_name:"Section Labels Notes", sidebar_type:'notes', subscreen:'section_labels', department_id:null, user_id:userInfo.user_id, help_link_path:`/app/${schoolType}/documentation/scheduling_wizard/create_sections/create_labels`});
	}, [userInfo, subscreenRestricted, subscreenLocked, schoolType, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo]);

	const sorted_labels = sortArrayOfObjects(labels, 'label', 'text', 'asc');
	const section_data_loading = isLoading || teachersLoading || coursesLoading || sectionsLoading;

	const main_sections = useMemo(() => {
		if(section_data_loading) return [];

		const subsection_ids = new Set(subsections.map(subsection_info => `${subsection_info.subsection_id}`));
		const logged_span_ids = new Set();

		return sections.reduce((result, section_info) => {
			if(`${section_info.section_type}` !== '1') return result;
			if(subsection_ids.has(`${section_info.section_id}`)) return result;

			const span_id = section_info.span_id;
			if(span_id && logged_span_ids.has(`${span_id}`)) return result;
			if(span_id) logged_span_ids.add(`${span_id}`);

			const section_course_info = courses.find(course_info => `${course_info.course_id}` === `${section_info.course_id}`);
			const section_teacher_info = teachers.find(teacher_info => `${teacher_info.teacher_id}` === `${section_info.teacher_id}`);

			const section_course_name = (section_course_info) ? capitalizeFirstLetters(section_course_info.name) : '';
			const section_course_code = (section_course_info) ? section_course_info.course_code : '';
			const section_teacher_name = (section_teacher_info) ? `${section_teacher_info.name}, ${section_teacher_info.first_name}` : '';

			result.push({
				...section_info,
				search_course_name: section_course_name,
				search_course_code: section_course_code,
				search_teacher_name: section_teacher_name,
				search_text: `${section_course_code} ${section_course_name} ${section_teacher_name}`.toLowerCase(),
			});

			return result;
		}, []);
	}, [section_data_loading, sections, subsections, courses, teachers]);

	const departmentOptions = useMemo(() => {
		const options = [{value:null, display:'All Departments'}];
		const included_department_ids = new Set();
		let includes_no_department = false;

		main_sections.forEach(section_info => {
			const section_departments = Array.isArray(section_info.departments) ? section_info.departments : [];
			if(section_departments.length === 0) includes_no_department = true;
			section_departments.forEach(department_id => included_department_ids.add(`${department_id}`));
		});

		if(includes_no_department) options.push({value:NO_DEPARTMENT_FILTER_VALUE, display:NO_DEPARTMENT_FILTER_DISPLAY});

		departments.forEach(department_info => {
			if(included_department_ids.has(`${department_info.department_id}`)) options.push({value:`${department_info.department_id}`, display:department_info.department});
		});

		return options;
	}, [departments, main_sections]);

	useEffect(() => {
		const included_teacher_ids = new Set();
		const included_course_ids = new Set();

		main_sections.forEach(section_info => {
			if(section_info.teacher_id !== null && section_info.teacher_id !== undefined) included_teacher_ids.add(`${section_info.teacher_id}`);
			if(section_info.course_id !== null && section_info.course_id !== undefined) included_course_ids.add(`${section_info.course_id}`);
		});

		const teacher_options = teachers.reduce((result, teacher_info) => {
			if(!included_teacher_ids.has(`${teacher_info.teacher_id}`)) return result;
			result.push({value:`${teacher_info.teacher_id}`, display:`${teacher_info.name}, ${teacher_info.first_name}`, selected:false});
			return result;
		}, []);

		const courseOptions = courses.reduce((result, course_info) => {
			if(!included_course_ids.has(`${course_info.course_id}`)) return result;
			result.push({value:`${course_info.course_id}`, display:`${capitalizeFirstLetters(course_info.name)} (${course_info.course_code})`, selected:false});
			return result;
		}, []);

		teacher_options.sort((a, b) => a.display.localeCompare(b.display));
		courseOptions.sort((a, b) => a.display.localeCompare(b.display));
		const team_label_options = sortArrayOfObjects(labels, 'label', 'text', 'asc').reduce((result, label_info) => {
			if(!label_info || !label_info.label_id || !label_info.label) return result;
			result.push({value:`${label_info.label_id}`, display:label_info.label, selected:false});
			return result;
		}, [{value:'none', display:'No team/label', selected:false}]);

		setFilterOptions((previous_options) => {
			const selected_teacher_values = new Set((previous_options.teachers || []).filter(option => option.selected).map(option => option.value));
			const selected_course_values = new Set((previous_options.courses || []).filter(option => option.selected).map(option => option.value));
			const selected_team_label_values = new Set((previous_options.teams_labels || []).filter(option => option.selected).map(option => option.value));

			return {
				teachers: teacher_options.map(option => ({...option, selected:selected_teacher_values.has(option.value)})),
				courses: courseOptions.map(option => ({...option, selected:selected_course_values.has(option.value)})),
				teams_labels: team_label_options.map(option => ({...option, selected:selected_team_label_values.has(option.value)})),
			};
		});
	}, [main_sections, teachers, courses, labels]);

	const filtered_sections = useMemo(() => {
		let results = [...main_sections];

		if(selectedDepartmentID)
		{
			results = results.filter((section_info) => {
				const section_departments = Array.isArray(section_info.departments) ? section_info.departments : [];
				if(isNoDepartmentFilterValue(selectedDepartmentID)) return section_departments.length === 0;
				return section_departments.findIndex(department_id => `${department_id}` === `${selectedDepartmentID}`) !== -1;
			});
		}

		const selected_teacher_values = new Set((filterOptions.teachers || []).filter(option => option.selected).map(option => option.value));
		if(selected_teacher_values.size > 0) results = results.filter(section_info => selected_teacher_values.has(`${section_info.teacher_id}`));

		const selected_course_values = new Set((filterOptions.courses || []).filter(option => option.selected).map(option => option.value));
		if(selected_course_values.size > 0) results = results.filter(section_info => selected_course_values.has(`${section_info.course_id}`));

		const selected_team_label_values = new Set((filterOptions.teams_labels || []).filter(option => option.selected).map(option => option.value));
		if(selected_team_label_values.size > 0)
		{
			const include_unlabeled_sections = selected_team_label_values.has('none');
			const selected_label_ids = [...selected_team_label_values].filter(value => value !== 'none');

			results = results.filter((section_info) => {
				const section_labels = Array.isArray(section_info.labels) ? section_info.labels : [];
				const subsection_ids = Array.isArray(section_info.subsections) ? section_info.subsections : [];
				const subsection_infos = subsection_ids.map((subsection_id) => sections.find(section => `${section.section_id}` === `${subsection_id}`)).filter(section => section);
				const subsection_labels = subsection_infos.reduce((result, subsection_info) => {
					if(!Array.isArray(subsection_info.labels)) return result;
					return result.concat(subsection_info.labels);
				}, []);
				const all_linked_labels = section_labels.concat(subsection_labels);
				const section_has_no_labels = all_linked_labels.length === 0;

				if(section_has_no_labels) return include_unlabeled_sections;
				return selected_label_ids.findIndex(label_id => all_linked_labels.findIndex(section_label_id => `${section_label_id}` === `${label_id}`) !== -1) !== -1;
			});
		}

		if(searchValue.length > 1) results = results.filter(section_info => section_info.search_text.includes(searchValue));

		return results;
	}, [main_sections, sections, selectedDepartmentID, filterOptions, searchValue]);

	const department_sections = useMemo(() => {
		if(section_data_loading) return [];
		const no_department_sections = filtered_sections.filter(section_info => !Array.isArray(section_info.departments) || section_info.departments.length === 0);
		const initial_results = (!selectedDepartmentID || isNoDepartmentFilterValue(selectedDepartmentID)) && no_department_sections.length > 0
			? [{department_id:NO_DEPARTMENT_FILTER_VALUE, department:NO_DEPARTMENT_FILTER_DISPLAY, sections:sortArrayOfObjects(no_department_sections, 'search_course_name', 'text', 'asc')}]
			: [];

		return departments.reduce((result, department_info) => {
			if(isNoDepartmentFilterValue(selectedDepartmentID)) return result;
			const department_id = `${department_info.department_id}`;
			if(selectedDepartmentID && selectedDepartmentID !== department_id) return result;

			const sections_in_department = filtered_sections.filter(section_info => (section_info.departments || []).findIndex(section_department_id => `${section_department_id}` === department_id) !== -1);
			if(sections_in_department.length === 0) return result;

			const sorted_sections = sortArrayOfObjects(sections_in_department, 'search_course_name', 'text', 'asc');
			result.push({department_id, department: department_info.department, sections: sorted_sections});
			return result;
		}, initial_results);
	}, [section_data_loading, departments, filtered_sections, selectedDepartmentID]);

	const current_user_is_admin = (userInfo.role_id === '1' || userInfo.role_id === '2' || userInfo.role_id === '5');
	const selected_filters = Object.keys(filterOptions).reduce((result, filter_title) => {
		const current_options = filterOptions[filter_title] || [];
		current_options.forEach((option) => {
			if(!option.selected) return;
			result.push({filter_title, value:option.value, display:option.display});
		});
		return result;
	}, []);

	const getFilterTitleDisplay = (filter_title) => {
		if(filter_title === 'teams_labels') return 'Teams/Labels';
		return filter_title.replace('_', ' ');
	};

	return (
		<>
		<h1 className='progress-main-header'>Section Labels</h1>
		<p className='progress-subheader' style={{width:'450px'}}>Add any teams/labels to your existing sections</p>
		{(subscreenRestricted) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to this screen</div>
			</div>
		):(subscreenLocked) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>Temporarily Locked</h1>
				<div>This screen is locked until all other prior steps are completed</div>
			</div>
		):(current_user_is_admin) ?
		(
			<div>
				<div style={{display:'grid', gridTemplateColumns:'1fr minmax(250px, auto)', columnGap:'50px', alignItems:'center', margin:'0px 0px 5px 0px'}}>
							<div className='mb-search-bar-container' style={{width:'100%'}}>
								<input
									className='mb-search-bar'
									style={{width:'100%'}}
									placeholder='Search by course code, course name, or teacher...'
									onChange={handleSearchChange}
								/>
								<FontAwesomeIcon className='mb-search-bar-icon' icon={faSearch}/>
								<div className='mb-search-bar-filter-container' ref={filter_ref}>
									<div style={{display:'grid', width:'100%', height:'100%', alignItems:'center', justifyItems:'center'}} onClick={() => setFilterDataVisible(!filterDataVisible)}>
										<FontAwesomeIcon icon={faSlidersH}/>
									</div>
									{filterDataVisible &&
										<div className='list-filter-options-container click-restricted' style={{top:'60px'}}>
											<div className='small-text'>Filter by:</div>
											{Object.keys(filterOptions).map((filter_title) => {
												const filter_options = filterOptions[filter_title] || [];
												const filter_options_open = openFilterOptions.includes(filter_title);
												const filter_group_selected_count = filter_options.filter((filter_option) => filter_option.selected).length;

												return (
													<div key={filter_title}>
														<div className='list-filter-title-container' onClick={() => updateOpenFilters(filter_title, filter_options_open)}>
															<div className='capitalize list-filter-title-label'>
																{getFilterTitleDisplay(filter_title)}
																{filter_group_selected_count > 0 &&
																	<span className='list-filter-title-selected-indicator'>{filter_group_selected_count}</span>
																}
															</div>
															<FontAwesomeIcon className='small-text' icon={filter_options_open ? faChevronUp : faChevronDown}/>
														</div>
														{filter_options_open &&
															filter_options.map((filter_option, option_index) => (
																<div className='parent-div list-filter-option click-restricted' key={`${filter_title}-${filter_option.value}`} onClick={() => updateFilterOptions(filter_title, option_index, !filter_option.selected)}>
																	<div className='list-filter-option-name click-restricted'>{filter_option.display}</div>
																	{filter_option.selected ? (
																		<FontAwesomeIcon className='fas-checkbox-checked click-restricted' icon={faCheckSquare}/>
																	) : (
																		<FontAwesomeIcon className='fas-checkbox-unchecked click-restricted' icon={faSquare}/>
																	)}
																</div>
															))
														}
													</div>
												);
											})}
										</div>
									}
								</div>
							</div>
							<div id='student-label-department-dropdown'>
								<Dropdown data={departmentOptions} currentValue={selectedDepartmentID} handleChange={(new_value) => setSelectedDepartmentID(normalizeFilterValue(new_value))} />
							</div>
				</div>
				<div className='mb-selected-filters-bar' style={{marginTop:'10px', marginBottom:'8px'}}>
							{selected_filters.length > 0 &&
								<>
									<div className='mb-selected-filters-text'>Filters:</div>
									{selected_filters.map((filter_option) => (
										<div className='mb-selected-filters-filter-tag' key={`${filter_option.filter_title}-${filter_option.value}`}>
											<div className='mb-selected-filters-filter-tag-inner-container'>
												<div>{filter_option.display}</div>
												<FontAwesomeIcon
													className='dark-gray-link'
													icon={faTimes}
													onClick={() => {
														const option_index = (filterOptions[filter_option.filter_title] || []).findIndex(option => option.value === filter_option.value);
														if(option_index !== -1) updateFilterOptions(filter_option.filter_title, option_index, false);
													}}
												/>
											</div>
										</div>
									))}
								</>
							}
				</div>

				<div id='create-labels-sections-container'>
							{section_data_loading &&
								<div className='section-creation-message-container'>
									<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'40px'}}/>
								</div>
							}
							{!section_data_loading && department_sections.length === 0 &&
								<div className='section-creation-message-container'>No sections matched your current filters.</div>
							}
							{!section_data_loading && department_sections.map((department_info) => {
								const department_sections_list = department_info.sections;

								return (
									<div key={department_info.department_id}>
										<h1 className='create-labels-department-heading'>{department_info.department}</h1>
										<div className='create-labels-sections-inner-container'>
											{department_sections_list.map((section_info) => {
												const section_id = section_info.section_id;
												const section_subsections = Array.isArray(section_info.subsections) ? section_info.subsections : [];

												const section_course_info = courses.find(course => `${course.course_id}` === `${section_info.course_id}`);
												const section_course_name = (section_course_info) ? capitalizeFirstLetters(section_course_info.name) : null;
												const section_course_code = (section_course_info) ? section_course_info.course_code : null;
												const section_semester = (section_course_info) ? section_course_info.semester : null;

												const section_teacher_info = teachers.find(teacher => `${teacher.teacher_id}` === `${section_info.teacher_id}`);
												const section_teacher_name = (section_teacher_info) ? `${section_teacher_info.name}, ${section_teacher_info.first_name}` : null;

												const section_labels = (section_info.labels || []).map(label_id => labels.find(label => label && (`${label.label_id}` === `${label_id}`))).filter(label => label);
												const sorted_section_labels = sortArrayOfObjects(section_labels, 'label', 'text', 'asc');

												return (
													<div className='subsection-review-section-container' key={section_id}>
														<div className='subsection-review-section-header dark-blue'>
															<div>Section ({section_semester})</div>
														</div>
															<div className='subsection-review-section-content'>
															<div className='subsection-review-edit-label'>Course</div>
															<div className='subsection-review-data tooltip' data-tooltip-text={`${section_course_name} (${section_course_code})`}>
																<div className='ellipsis'>{section_course_name} ({section_course_code})</div>
															</div>
															<div className='subsection-review-edit-label'>Teacher</div>
															<div className='subsection-review-data tooltip' data-tooltip-text={`${section_teacher_name}`}>
																<div className='ellipsis'>{section_teacher_name}</div>
															</div>
																{sorted_section_labels.map((label) => {
																	const action_key = `${section_id}-${label.label_id}`;
																	const removing_label = !!pendingSectionLabelRemoves[action_key];

																	if(removing_label)
																	{
																		return (
																			<div className='create-labels-section-label create-labels-section-label-loading' key={action_key}>
																				<img src={require('../../../images/balls.gif')} alt='removing...' style={{height:'24px'}}/>
																			</div>
																		);
																	}

																	return (
																		<div className='create-labels-section-label' key={action_key}>
																			<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																			<div>{label.label}</div>
																			<FontAwesomeIcon className='cancel-link' icon={faTimes} onClick={() => removeSectionLabel(section_id, label.label_id)}/>
																		</div>
																	);
																})}
																<div className='add-label-to-section blue-link small-text click-restricted' onClick={() => setOpenLabelOptionsSectionID((current_id) => `${current_id}` === `${section_id}` ? null : section_id)}>Add Team/Label</div>
																<div className='create-labels-label-options-container' ref={`${openLabelOptionsSectionID}` === `${section_id}` ? label_options_ref : null}>
																	{`${openLabelOptionsSectionID}` === `${section_id}` &&
																		<div className='data-options-container'>
																			<div className='data-options-heading'>Teams/Labels</div>
																		{sorted_labels.length === 0 &&
																			<div className='create-labels-no-label-options'>No teams/labels added yet...</div>
																		}
																			{sorted_labels.map((label) => {
																				const action_key = `${section_id}-${label.label_id}`;
																				const adding_label = !!pendingSectionLabelAdds[action_key];
																				return (
																					<div
																						className={`data-options-option create-labels-label-option ${adding_label ? 'create-labels-label-option-loading' : ''}`}
																						key={`${section_id}-option-${label.label_id}`}
																						onClick={() => !adding_label && addSectionLabel(section_id, label.label_id)}
																					>
																						{adding_label ?
																							<img src={require('../../../images/balls.gif')} alt='adding...' style={{height:'24px'}}/> :
																							<>
																								<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																								<div>{label.label}</div>
																							</>
																						}
																					</div>
																				);
																			})}
																		</div>
																	}
																</div>

															{section_subsections.map((subsection_id) => {
																const subsection_info = subsections.find(subsection => `${subsection.subsection_id}` === `${subsection_id}`);
																const subsection_section_info = sections.find(section => `${section.section_id}` === `${subsection_id}`);
																if(!subsection_info || !subsection_section_info) return null;

																const subsection_course_info = courses.find(course => `${course.course_id}` === `${subsection_info.course_id}`);
																const subsection_course_name = (subsection_course_info) ? capitalizeFirstLetters(subsection_course_info.name) : null;
																const subsection_course_code = (subsection_course_info) ? subsection_course_info.course_code : null;

																const subsection_teacher_info = teachers.find(teacher => `${teacher.teacher_id}` === `${subsection_info.teacher_id}`);
																const subsection_teacher_name = (subsection_teacher_info) ? `${subsection_teacher_info.name}, ${subsection_teacher_info.first_name}` : null;

																const subsection_is_inclusion = subsection_section_info.is_inclusion;
																let is_placeholder_inclusion = false;
																if(scheduleInfo && `${scheduleInfo.separate_ICR_codes}` === '0' && `${section_info.course_id}` === `${subsection_info.course_id}` && `${subsection_is_inclusion}` === '1') is_placeholder_inclusion = true;

																const subsection_labels = (subsection_section_info.labels || []).map(label_id => labels.find(label => label && (`${label.label_id}` === `${label_id}`))).filter(label => label);
																const sorted_subsection_labels = sortArrayOfObjects(subsection_labels, 'label', 'text', 'asc');

																return (
																	<div className='subsection-review-section-container' key={`${section_id}-${subsection_id}`}>
																		<div className={`subsection-review-section-header ${is_placeholder_inclusion ? 'yellow' : 'blue'}`}>
																			<div>{is_placeholder_inclusion && 'Inclusion'} Subsection</div>
																		</div>
																		<div className='subsection-review-section-content'>
																			{!is_placeholder_inclusion &&
																				<>
																				<div className='subsection-review-edit-label'>Course</div>
																				<div className='subsection-review-data tooltip' data-tooltip-text={`${subsection_course_name} (${subsection_course_code})`}>
																					<div className='ellipsis'>{subsection_course_name} ({subsection_course_code})</div>
																				</div>
																				</>
																			}
																			<div className='subsection-review-edit-label'>Teacher</div>
																			<div className='subsection-review-data tooltip' data-tooltip-text={`${subsection_teacher_name}`}>
																				<div className='ellipsis'>{subsection_teacher_name}</div>
																			</div>
																				{sorted_subsection_labels.map((label) => {
																					const action_key = `${subsection_id}-${label.label_id}`;
																					const removing_label = !!pendingSectionLabelRemoves[action_key];

																					if(removing_label)
																					{
																						return (
																							<div className='create-labels-section-label create-labels-section-label-loading' key={action_key}>
																								<img src={require('../../../images/balls.gif')} alt='removing...' style={{height:'24px'}}/>
																							</div>
																						);
																					}

																					return (
																						<div className='create-labels-section-label' key={action_key}>
																							<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																							<div>{label.label}</div>
																							{(!is_placeholder_inclusion || label.label !== 'Inclusion') &&
																								<FontAwesomeIcon className='cancel-link' icon={faTimes} onClick={() => removeSectionLabel(subsection_id, label.label_id)}/>
																							}
																						</div>
																					);
																				})}
																				<div className='add-label-to-section blue-link small-text click-restricted' onClick={() => setOpenLabelOptionsSectionID((current_id) => `${current_id}` === `${subsection_id}` ? null : subsection_id)}>Add Team/Label</div>
																				<div className='create-labels-label-options-container' ref={`${openLabelOptionsSectionID}` === `${subsection_id}` ? label_options_ref : null}>
																					{`${openLabelOptionsSectionID}` === `${subsection_id}` &&
																						<div className='data-options-container'>
																							<div className='data-options-heading'>Teams/Labels</div>
																						{sorted_labels.length === 0 &&
																							<div className='create-labels-no-label-options'>No teams/labels added yet...</div>
																						}
																							{sorted_labels.map((label) => {
																								const action_key = `${subsection_id}-${label.label_id}`;
																								const adding_label = !!pendingSectionLabelAdds[action_key];
																								return (
																									<div
																										className={`data-options-option create-labels-label-option ${adding_label ? 'create-labels-label-option-loading' : ''}`}
																										key={`${subsection_id}-option-${label.label_id}`}
																										onClick={() => !adding_label && addSectionLabel(subsection_id, label.label_id)}
																									>
																										{adding_label ?
																											<img src={require('../../../images/balls.gif')} alt='adding...' style={{height:'24px'}}/> :
																											<>
																												<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																												<div>{label.label}</div>
																											</>
																										}
																									</div>
																								);
																							})}
																						</div>
																					}
																				</div>
																		</div>
																	</div>
																);
															})}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								);
							})}
				</div>
			</div>
		):(userInfo && !current_user_is_admin) ?
		(
			<div className='locked-screen-notification-container'>
				<FontAwesomeIcon className='blue-text' icon={faLock} style={{fontSize:'50px'}}/>
				<h1>Locked</h1>
				<p>This step is only accessible to the master scheduler. You should be notified when future steps are unlocked!</p>
				<Link to='/progress' className='blue-text'>Go Back</Link>
			</div>
		): null}
		{(!subscreenLocked && !subscreenRestricted && current_user_is_admin) &&
			<div className='fixed-bottom-save-container'>
				<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Continue to Place Sections' onClickFunction={handleMoveToNextScreen} />
			</div>
		}
		</>
	);
}
